import { useHybridFocusable } from '../../hooks/useHybridFocusable'
import cx from 'classnames'
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useSidePanel } from './SidePanelProvider'
import { useSidePanelPrivate } from './useSidePanelPrivate'
import { useNavigationMode } from '../../../../shared/providers/NavigationModeProvider/NavigationModeProvider'
import tts from '../../lib/TTS'

import type { SidePanelItemProps } from './types'
import type { FocusHandler } from '@noriginmedia/norigin-spatial-navigation'

const SidePanelItem = ({ title, to, active }: SidePanelItemProps) => {
  const { isSidePanelFocused } = useSidePanel()
  const { onItemFocus } = useSidePanelPrivate()
  const { isCursorMode } = useNavigationMode()

  const handleItemFocus: FocusHandler<{ to: string }> = (_, { to }) => {
    tts.stopRead()
    tts.addTextToRead(title)
    onItemFocus(to)
  }

  const { ref, focused, isHovered, focusSelf } = useHybridFocusable({
    focusKey: to,
    onFocus: handleItemFocus,
    onArrowPress: (direction) => direction !== 'right' || active,
    extraProps: { to },
    onEnterPress: () => focusSelf(),
  })

  const isActiveUnfocused = useMemo(() => {
    const isFocusedOrHovered = focused || isHovered
    if (isCursorMode) {
      return active && !isFocusedOrHovered
    } else {
      return active && !isSidePanelFocused && !isFocusedOrHovered
    }
  }, [active, isSidePanelFocused, focused, isHovered, isCursorMode])

  return (
    <li
      className={cx('side-panel-item', {
        'side-panel-item--focused': focused || isHovered,
        'side-panel-item--active-unfocused': isActiveUnfocused,
      })}
    >
      <Link to={to} ref={ref} className="side-panel-item__link">
        {title}
      </Link>
    </li>
  )
}

export default SidePanelItem
